<template>
	<div class="dashboard-gestao">
		<DashboardGestaoHeader
			:filial="filial"
			:periodo="periodo"
			:periodo-min="periodoDisponivel.min"
			:periodo-max="periodoDisponivel.max"
			:export-disabled="loading || !model || !cur.length"
			:filiais="filiais"
			@update:filial="filial = $event"
			@update:periodo="periodo = $event"
			@limit-exceeded="onPeriodoLimitExceeded"
			@export="exportDashboard"
		/>

		<div v-if="loading" class="dg-loading">
			<i class="fas fa-spinner fa-spin"></i>
			<p>{{ $t('loadingOoo') }}</p>
		</div>

		<div v-else-if="!hasData" class="dg-empty">
			<i class="far fa-calendar-times"></i>
			<strong>{{ $t('dashboard_gestao_empty_title') }}</strong>
			<p>{{ $t('dashboard_gestao_empty_text') }}</p>
		</div>

		<template v-else>
			<DashboardGestaoKpis :kpis="kpis" />

			<DashboardGestaoAnalyticsStatus v-if="analyticsStatusEnabled" />

			<DashboardGestaoRowDesempenho
			:ranking="ranking"
			:filial="filial"
			:cur="cur"
			@select-filial="filial = $event"
			/>
			
			<DashboardGestaoRowEvolucao :cur="cur" :funnel="funnel" />

			<DashboardGestaoProblemas :cur="cur" />
		</template>
	</div>
</template>

<script>
import DashboardGestaoController from '@/views/controllers/DashboardGestaoController';
import DashboardGestaoService from '@/application/services/DashboardGestao';
import ExcelBuilder from '@/application/libs/excelBuilder';
import Util from '@/application/libs/util';
import DashboardGestaoHeader from './dashboardGestao/DashboardGestaoHeader.vue';
import DashboardGestaoKpis from './dashboardGestao/DashboardGestaoKpis.vue';
import DashboardGestaoAnalyticsStatus from './dashboardGestao/DashboardGestaoAnalyticsStatus.vue';
import DashboardGestaoRowEvolucao from './dashboardGestao/DashboardGestaoRowEvolucao.vue';
import DashboardGestaoRowDesempenho from './dashboardGestao/DashboardGestaoRowDesempenho.vue';
import DashboardGestaoProblemas from './dashboardGestao/DashboardGestaoProblemas.vue';

export default {
	name: 'DashboardGestao',
	components: {
		DashboardGestaoHeader,
		DashboardGestaoKpis,
		DashboardGestaoAnalyticsStatus,		
		DashboardGestaoRowEvolucao,
		DashboardGestaoRowDesempenho,
		DashboardGestaoProblemas,
	},
	data() {
		return {
			model: null,
			loading: true,
			filial: DashboardGestaoService.GERAL_KEY,
			periodo: { start: '', end: '' },
		};
	},
	computed: {
		catalog() {
			return DashboardGestaoController.getCatalog(this.model);
		},
		filiais() {
			return this.catalog.filiais;
		},
		rowsAll() {
			return DashboardGestaoController.getMonthlyRows(this.model, this.filial);
		},
		rowsGeral() {
			return DashboardGestaoController.getMonthlyRows(this.model, DashboardGestaoService.GERAL_KEY);
		},
		periodoDisponivel() {
			return DashboardGestaoController.getAvailableRange(this.rowsGeral);
		},
		cur() {
			return DashboardGestaoController.filterRowsByPeriod(this.rowsAll, this.periodo);
		},
		prev() {
			return DashboardGestaoController.filterRowsByPreviousPeriod(this.rowsAll, this.periodo);
		},
		currentPeriod() {
			return DashboardGestaoController.calcPeriodAggregate(this.cur);
		},
		previousPeriod() {
			return DashboardGestaoController.calcPeriodAggregate(this.prev);
		},
		kpis() {
			return DashboardGestaoController.buildKpiList(this.currentPeriod, this.previousPeriod);
		},
		funnel() {
			return DashboardGestaoController.getConfidenceFunnel(this.currentPeriod);
		},
		ranking() {
			return DashboardGestaoController.getFilialRanking(this.model, this.periodo);
		},
		analyticsStatusEnabled() {
			return !!process.env.GERADORANALYTICS_STATUS_URL;
		},
		hasData() {
			return this.cur.length > 0;
		},
	},
	watch: {
		model() {
			this.ensureValidPeriodo();
		},
	},
	created() {
		this.loadDashboard();
	},
	methods: {
		loadDashboard() {
			this.loading = true;
			DashboardGestaoService.getDashboardModel()
			.then(model => {
					this.model = model;
					this.ensureValidFilial();
					this.ensureValidPeriodo();
				})
				.catch(() => {
					this.model = null;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		ensureValidFilial() {
			const isKnownFilial = this.filiais.some(f => f.id === this.filial);
			if (!isKnownFilial) this.filial = DashboardGestaoService.GERAL_KEY;
		},
		ensureValidPeriodo() {
			this.periodo = DashboardGestaoController.normalizeRange(this.periodo, this.periodoDisponivel);
		},
		onPeriodoLimitExceeded() {
			Util.showWarningNotification({
				title: this.$t('dashboard_gestao_period_limit_title'),
				text: this.$t('dashboard_gestao_period_limit_text'),
			});
		},
		formatMes(mesKey) {
			if (!mesKey) return '';

			const [year, month] = mesKey.split('-').map(Number);

			if (!year || !month || month < 1 || month > 12) return '';

			const MONTH_KEYS = [
				'jan',
				'fev',
				'mar',
				'abr',
				'mai',
				'jun',
				'jul',
				'ago',
				'set',
				'out',
				'nov',
				'dez',
			];

			const monthKey = MONTH_KEYS[month - 1];

			return `${this.$t(`month_short_${monthKey}`)}/${year}`;
		},
		exportDashboard() {
			// colocar campo de periodo !!
			if (this.loading || !this.model || !this.cur.length) return;

			const filialSelecionada = this.filial === DashboardGestaoService.GERAL_KEY
				? this.$t('dashboard_gestao_select_geral')
				: (this.filiais.find(f => f.id === this.filial) || {}).nome || this.filial;
			const periodoSelecionado = `${this.formatMes(this.periodo.start)} a ${this.formatMes(this.periodo.end)}`;

			const headerDetails = [
				{ label: this.$t('dashboard_gestao_export_filtro_filial'), value: filialSelecionada },
				{ label: this.$t('dashboard_gestao_export_filtro_periodo'), value: periodoSelecionado },
				{ label: this.$t('dashboard_gestao_export_gerado_em'), value: new Date().toLocaleString('pt-BR') },
			];
			const tabs = DashboardGestaoController.buildDashboardExportTabs({
				cur: this.cur,
				kpis: this.kpis,
				ranking: this.ranking,
				funnel: this.funnel,
				headerDetails,
			});

			ExcelBuilder.fromJsonMultipleTabs(
				tabs,
				`dashboard_gestao_${this.filial}`,
			);
		},
	},
};
</script>

<style scoped lang="less">
@bg: #f3f4f8;
@ink: #1a2036;
@fontFamily: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

.dashboard-gestao {
	background: @bg;
	color: @ink;
	font-family: @fontFamily;
	padding: 20px 26px 40px;
	min-height: calc(100vh - 53px);
	overflow: auto;
}

.dg-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 60px 0;
	color: #6b7280;
	font-size: 13px;

	i { font-size: 22px; }
	p { margin: 0; }
}

.dg-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 48px 20px;
	border: 1px dashed #d5dae6;
	border-radius: 14px;
	background: #ffffff;
	color: #6b7280;
	text-align: center;

	i {
		font-size: 24px;
		color: #94a3b8;
	}

	strong {
		font-size: 14px;
		color: #1a2036;
	}

	p {
		margin: 0;
		font-size: 12px;
		max-width: 380px;
	}
}
</style>
